import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, TextField, MenuItem, Checkbox, FormControlLabel, Button, Divider } from '@mui/material';
import { parseSkills } from '../utils/skills';

const PlayerDetail = ({ open, onClose, identifier, onSaved }) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({ group: 'user', warnings: 0, banned: false, banneduntil: 0 });

    useEffect(() => {
        if (!open || !identifier) return;

        const fetchData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/admin/users/${identifier}`, {
                    credentials: 'include'
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch player data");
                }
                const result = await response.json();
                setData(result);
                setForm({
                    group: result.user?.group || 'user',
                    warnings: result.user?.warnings || 0,
                    banned: !!result.user?.banned,
                    banneduntil: result.user?.banneduntil || 0
                });
            } catch (err) {
                setError(err.message);
                console.error("Error fetching player data:", err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [open, identifier]);

    const handleSave = async () => {
        try {
            const response = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/admin/users/${identifier}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    group: form.group,
                    warnings: Number(form.warnings),
                    banned: form.banned ? 1 : 0,
                    banneduntil: Number(form.banneduntil)
                })
            });
            if (!response.ok) {
                throw new Error("Failed to update user");
            }
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            setError(err.message);
            console.error("Error updating user:", err);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 600,
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                maxHeight: '90vh',
                overflow: 'auto'
            }}>
                <Typography variant="h6" component="h2">
                    {identifier}
                </Typography>

                {isLoading ? (
                    <Typography sx={{ mt: 2 }}>Loading...</Typography>
                ) : error ? (
                    <Typography sx={{ mt: 2 }} color="error">{error}</Typography>
                ) : (
                    <>
                        <Typography variant="subtitle1" sx={{ mt: 3 }}>Characters</Typography>
                        {data.characters.length === 0 ? (
                            <Typography sx={{ mt: 1 }} color="text.secondary">No characters</Typography>
                        ) : (
                            data.characters.map((character) => {
                                const skills = parseSkills(character.skills);
                                return (
                                    <Box key={character.charidentifier} sx={{ mt: 1 }}>
                                        <Typography>{character.firstname} {character.lastname} — {character.joblabel}</Typography>
                                        {skills && (
                                            <Typography variant="body2" color="text.secondary">
                                                Mining {skills.mining.level} · Crafting {skills.crafting.level} · Hunting {skills.hunting.level} · Fishing {skills.fishing.level}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })
                        )}

                        <Typography variant="subtitle1" sx={{ mt: 3 }}>Properties</Typography>
                        {data.properties.length === 0 ? (
                            <Typography sx={{ mt: 1 }} color="text.secondary">No properties</Typography>
                        ) : (
                            data.properties.map((property) => (
                                <Typography key={property.id} sx={{ mt: 1 }}>
                                    {property.common_name} — ${property.price} {property.owned ? '(owned)' : '(unowned)'}
                                </Typography>
                            ))
                        )}

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1">Moderation</Typography>
                        <TextField
                            select
                            label="Group"
                            value={form.group}
                            onChange={(e) => setForm({ ...form, group: e.target.value })}
                            fullWidth
                            sx={{ mt: 2 }}
                        >
                            <MenuItem value="user">user</MenuItem>
                            <MenuItem value="realtor">realtor</MenuItem>
                            <MenuItem value="admin">admin</MenuItem>
                        </TextField>
                        <TextField
                            label="Warnings"
                            type="number"
                            value={form.warnings}
                            onChange={(e) => setForm({ ...form, warnings: e.target.value })}
                            fullWidth
                            sx={{ mt: 2 }}
                        />
                        <FormControlLabel
                            sx={{ mt: 1 }}
                            control={
                                <Checkbox
                                    checked={form.banned}
                                    onChange={(e) => setForm({ ...form, banned: e.target.checked })}
                                />
                            }
                            label="Banned"
                        />
                        <TextField
                            label="Banned Until (unix timestamp)"
                            type="number"
                            value={form.banneduntil}
                            onChange={(e) => setForm({ ...form, banneduntil: e.target.value })}
                            fullWidth
                            sx={{ mt: 2 }}
                        />

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={onClose}>Cancel</Button>
                            <Button variant="contained" onClick={handleSave}>Save</Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default PlayerDetail;
